import { session } from '@utils/storage';
import { FC } from 'react';

const getStorageKey = (name: LazyRetryNames): string => {
    return `retry-lazy-refreshed-${name}`;
};

// a function to retry loading a chunk to avoid chunk load error for out of date code
const lazyRetry = <T>(componentImport: () => Promise<{ readonly default: FC<T> }>, name: LazyRetryNames): Promise<{ readonly default: FC<T> }> => {
    return new Promise((resolve, reject) => {
        // check if the window has already been refreshed
        const hasRefreshed = JSON.parse(session.getItem(getStorageKey(name)) || 'false');
        // try to import the component
        componentImport()
            .then((component) => {
                session.removeItem(getStorageKey(name)); // success so reset the refresh
                resolve(component);
            })
            .catch((error) => {
                if (!hasRefreshed) {
                    // not been refreshed yet
                    session.setItem(getStorageKey(name), 'true'); // we are now going to refresh
                    return window.location.reload(); // refresh the page
                }
                return reject(error); // Default error behaviour as already tried refresh
            });
    });
};

export enum LazyRetryNames {
    Dashboard = 'Dashboard',
    Releases = 'Releaes',
    List = 'List',
    Document = 'Document',
    DocumentExternalView = 'DocumentExternalView',
    ModalExternalView = 'ModalExternalView',
    FirstDocumentPage = 'FirstDocumentPage',
    AllDocumentProcessed = 'AllDocumentProcessed',
    Settings = 'Settings',
    GeneralSettings = 'GeneralSettings',
    Configurations = 'Configurations',
    Support = 'Support',
    Search = 'Search',
    FileHandler = 'FileHandler',
    TechnicalInformation = 'TechnicalInformation',
    Faq = 'Faq',
    Videos = 'Videos',
    Introduction = 'Introduction',
    Contact = 'Contact',
    ReleasesHome = 'ReleasesHome',
    ReleasesSam = 'ReleasesSam',
    ModalAddPagesToDocument = 'ModalAddPagesToDocument',
    ModalAdministrations = 'ModalAdministrations',
    ModalAnnotation = 'ModalAnnotation',
    ModalCodePreview = 'ModalCodePreview',
    ModalCodingAccruals = 'ModalCodingAccruals',
    ModalConfirm = 'ModalConfirm',
    ModalCopyCoding = 'ModalCopyCoding',
    ModalCreditorValidation = 'ModalCreditorValidation',
    ModalDeleteOcrTraining = 'ModalDeleteOcrTraining',
    ModalDocumentAttachments = 'ModalDocumentAttachments',
    ModalDocumentAudit = 'ModalDocumentAudit',
    ModalDocumentPreview = 'ModalDocumentPreview',
    ModalDocumentValidation = 'ModalDocumentValidation',
    ModalDynamicAssign = 'ModalDynamicAssign',
    ModalEditDocument = 'ModalEditDocument',
    ModalEmailDocument = 'ModalEmailDocument',
    ModalEmailPreview = 'ModalEmailPreview',
    ModalGridSettings = 'ModalGridSettings',
    ModalLinkDocuments = 'ModalLinkDocuments',
    ModalList = 'ModalList',
    ModalOcrCodingTraining = 'ModalOcrCodingTraining',
    ModalOcrTrainingField = 'ModalOcrTrainingField',
    ModalOcrTrainingLog = 'ModalOcrTrainingLog',
    ModalOcrTrainingSql = 'ModalOcrTrainingSql',
    ModalPerformDocumentAction = 'ModalPerformDocumentAction',
    ModalRefreshLookup = 'ModalRefreshLookup',
    ModalRepairDocument = 'ModalRepairDocument',
    ModalReplacement = 'ModalReplacement',
    ModalSamBackOfficeStatusHelper = 'ModalSamBackOfficeStatusHelper',
    ModalSamFeedback = 'ModalSamFeedback',
    ModalSamLineSource = 'ModalSamLineSource',
    ModalSamLineSourceMore = 'ModalSamLineSourceMore',
    ModalSamIntroduction = 'ModalSamIntroduction',
    ModalTemplateSharingManagement = 'ModalTemplateSharingManagement',
    ModalText = 'ModalText',
    ModalUserReplaced = 'ModalUserReplaced',
    ModalWorkflowActionValidation = 'ModalWorkflowActionValidation',
}

export default lazyRetry;
